import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';

import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';

import {
    CACHE_CONFIG,
    ConfigService as AbstractConfigService,
    CORE_APP_MODULES,
    ShCoreAppModule,
    ShDynamicDialogService,
    ShFieldDefinitionService,
    ShTaskAutocompleteDataService,
    ShTaskFacetDataService
} from '@stylehaven/components';

import {
    AUTOLOGIN,
    ConfigService,
    CoreHttpService,
    DigihavenAuthenticationService,
    DigihavenTaskAutocompleteDataService,
    DigihavenTaskFacetDataService,
    FieldDefinitionService,
    paramEncodingInterceptorProvider,
    RecordNavigationService,
    SharedModule,
    SharedToastModule,
    ToastService,
    WindowService
} from '@digihaven/shared';

import { BootstrapRoutingModule } from '@bootstrap/bootstrap-routing.module';
import { RootContainerComponent } from '@bootstrap/containers/root-container.component';
import { AuthGuard } from '@bootstrap/guards/auth/auth.guard';
import { BootstrapSandboxService } from '@bootstrap/sandbox/bootstrap.sandbox.service';
import { localStorageSyncReducer } from '@common/helpers/local-storage-syncer.helper';
import { tokenInterceptorProviders } from '@common/interceptors/refresh-token';
import { AuthorizationService } from '@common/services/authorization/authorization.service';
import { HttpService } from '@common/services/http/http.service';
import { LocaleService } from '@common/services/locale/locale.service';
import { NavigationService } from '@common/services/navigation/navigation.service';
import { rootReducer } from '@reducers/root.reducer';

import { EnvironmentConfig } from '@common/entities/common/environment-config.entity';
import { APP_NAME_TOKEN, AppName, CustomTranslateLoader, LocaleCode, SharedLanguageService } from '@language';
import { NgxPrinterModule } from 'ngx-printer';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';

const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

@NgModule({
    declarations: [RootContainerComponent],
    imports: [
        // Core App imports
        ...CORE_APP_MODULES,
        ShCoreAppModule.forRoot(CoreHttpService),
        BootstrapRoutingModule,
        StoreModule.forRoot(rootReducer, { metaReducers }),

        // DH Shared imports
        SharedModule.forRoot(HttpService, NavigationService),
        SharedToastModule,

        // App specific imports
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [APP_NAME_TOKEN]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        }),
        NgxPrinterModule.forRoot({
            printOpenWindow: false,
            printPreviewOnly: false,
            timeToWaitRender: 200,
            renderClass: 'dgh-print-paper-container',
            appRootName: 'archive-mgmt-root'
        })
    ],
    providers: [
        // #region Translations and Locales
        {
            provide: APP_NAME_TOKEN,
            useFactory: (): AppName => {
                return 'archive-management';
            }
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (language: SharedLanguageService, config: ConfigService<EnvironmentConfig>) => () =>
                config.load('assets/environment.json').then(() => language.init('nl_BE' as LocaleCode)),
            deps: [SharedLanguageService, ConfigService, HttpClient],
            multi: true
        },
        {
            provide: LOCALE_ID,
            useValue: 'nl_BE'
        },
        // #endregion
        AuthGuard,
        {
            provide: AUTOLOGIN,
            useValue: 'digitaalarchief'
        },
        AuthorizationService,
        BootstrapSandboxService,
        ConfigService,
        LocaleService,
        HttpService,
        {
            provide: APP_INITIALIZER,
            useFactory: (locale: LocaleService) => () => locale.init(),
            deps: [LocaleService],
            multi: true
        },
        paramEncodingInterceptorProvider,
        tokenInterceptorProviders,
        ToastService,
        DigihavenAuthenticationService,
        WindowService,
        ShDynamicDialogService,
        {
            provide: FieldDefinitionService,
            deps: [HttpService]
        },
        {
            provide: RecordNavigationService,
            useClass: NavigationService
        },
        {
            provide: ShFieldDefinitionService,
            useClass: FieldDefinitionService
        },
        {
            provide: ShTaskFacetDataService,
            useClass: DigihavenTaskFacetDataService
        },
        {
            provide: ShTaskAutocompleteDataService,
            useClass: DigihavenTaskAutocompleteDataService
        },
        {
            provide: AbstractConfigService,
            useClass: ConfigService
        },
        {
            provide: CACHE_CONFIG,
            useValue: [{ url: /profiles(\/|\?)/ }]
        }
    ],
    bootstrap: [RootContainerComponent]
})
export class BootstrapModule {}
